import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Recruit, RosterManageEntry, RosterSection, TurnedAway, UploadedFlag, User } from '@clan-heart/shared';
import { canRemove } from '@clan-heart/shared';
import {
  approveRecruit,
  createRosterEntry,
  deleteRosterEntry,
  deleteUploadedFlag,
  getRecruits,
  getRosterManage,
  getTurnedAway,
  getUploadedFlags,
  readmitCrew,
  rejectRecruit,
  reorderRoster,
  updateRosterEntry,
} from '../api.js';
import { Button } from '../components/Button.js';
import { FlagPicker } from '../components/FlagPicker.js';
import { SECTION_LABELS, SECTION_ORDER, filterEntries, groupBySection, profileHref } from './crewList.js';
import styles from './CrewPage.module.css';

const STANDARD_FLAG = '/art/flags/flag-heart-standard-clean.webp';

/** Whether `viewer` may unlink or remove `entry` — the same rule the server
 * applies, using the linked account's role (null when unlinked). */
function canActOnLink(viewerRole: User['role'], entry: RosterManageEntry): boolean {
  return canRemove(viewerRole, entry.linkedRole);
}

/** An uploaded flag, who flies it, and a way to remove it once nobody does. */
function UploadedFlagRow({ uploaded }: { uploaded: UploadedFlag }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const remove = useMutation({
    mutationFn: () => deleteUploadedFlag(uploaded.flag),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['uploaded-flags'] });
      queryClient.invalidateQueries({ queryKey: ['flags'] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Something went wrong.'),
  });
  const inUse = uploaded.usedBy.length > 0;

  function handleRemove() {
    setError(null);
    if (!window.confirm('Remove this flag for good? It can’t be brought back.')) return;
    remove.mutate();
  }

  return (
    <div className={styles.flagRow}>
      <img className={styles.flagImage} src={uploaded.flag} alt="" />
      <div className={styles.flagInfo}>
        <span className={styles.meta}>
          {inUse ? `Flown by ${uploaded.usedBy.join(', ')}` : 'Not in use'} · uploaded{' '}
          {new Date(uploaded.createdAt).toLocaleDateString()}
        </span>
        {error && <span className={styles.error}>{error}</span>}
      </div>
      <Button
        type="button"
        variant="danger"
        disabled={inUse || remove.isPending}
        title={inUse ? 'Give them another flag first' : undefined}
        onClick={handleRemove}
      >
        Remove
      </Button>
    </div>
  );
}

function UploadedFlags() {
  const query = useQuery({ queryKey: ['uploaded-flags'], queryFn: getUploadedFlags });
  const flags = query.data?.flags ?? [];
  return (
    <>
      {query.isError && <p className={styles.error}>{(query.error as Error).message}</p>}
      {query.isSuccess && flags.length === 0 && <p className={styles.empty}>No flags uploaded yet.</p>}
      {flags.length > 0 && (
        <p className={styles.meta}>A flag can only be removed once nobody in the Hall flies it.</p>
      )}
      <div className={styles.list}>
        {flags.map((f) => (
          <UploadedFlagRow key={f.flag} uploaded={f} />
        ))}
      </div>
    </>
  );
}

/** Someone rejected or removed — with a way back aboard if it was a mistake. */
function TurnedAwayRow({ person }: { person: TurnedAway }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const readmit = useMutation({
    mutationFn: () => readmitCrew(person.id),
    onSuccess: () => {
      for (const key of [['turned-away'], ['roster'], ['members'], ['recruits']]) {
        queryClient.invalidateQueries({ queryKey: key });
      }
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Something went wrong.'),
  });

  function handleReadmit() {
    if (!window.confirm(`Let ${person.displayName} aboard after all? They get a place on the roster and cave access.`)) return;
    readmit.mutate();
  }

  const what = person.status === 'removed' ? 'Removed' : 'Rejected';
  const by = person.decidedBy ? ` by ${person.decidedBy}` : '';
  const when = person.decidedAt ? ` on ${new Date(person.decidedAt).toLocaleDateString()}` : '';

  return (
    <article className={styles.row}>
      <div className={styles.rowTop}>
        <div className={styles.member}>
          {person.avatarUrl ? <img className={styles.avatar} src={person.avatarUrl} alt="" /> : null}
          <span className={styles.title}>{person.displayName}</span>
        </div>
        <span className={styles.meta}>
          {what}
          {by}
          {when}
        </span>
      </div>
      {error && <p className={styles.error}>{error}</p>}
      <div className={styles.actions}>
        <Button type="button" variant="primary" disabled={readmit.isPending} onClick={handleReadmit}>
          Let aboard
        </Button>
      </div>
    </article>
  );
}

function RecruitRow({ recruit }: { recruit: Recruit }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['recruits'] });
    queryClient.invalidateQueries({ queryKey: ['roster', 'manage'] });
    queryClient.invalidateQueries({ queryKey: ['members'] });
    queryClient.invalidateQueries({ queryKey: ['turned-away'] });
  }

  const decide = useMutation({
    mutationFn: (action: 'approve' | 'reject') =>
      action === 'approve' ? approveRecruit(recruit.id) : rejectRecruit(recruit.id),
    onSuccess: invalidate,
    onError: (err) => setError(err instanceof Error ? err.message : 'Something went wrong.'),
  });

  return (
    <article className={styles.row}>
      <div className={styles.rowTop}>
        <div className={styles.member}>
          {recruit.avatarUrl ? <img className={styles.avatar} src={recruit.avatarUrl} alt="" /> : null}
          <span className={styles.title}>{recruit.displayName}</span>
        </div>
        <span className={styles.meta}>requested {new Date(recruit.createdAt).toLocaleDateString()}</span>
      </div>
      {error && <p className={styles.error}>{error}</p>}
      <div className={styles.actions}>
        <Button
          type="button"
          variant="primary"
          disabled={decide.isPending}
          onClick={() => decide.mutate('approve')}
        >
          Approve
        </Button>
        <Button
          type="button"
          variant="danger"
          disabled={decide.isPending}
          onClick={() => decide.mutate('reject')}
        >
          Reject
        </Button>
      </div>
    </article>
  );
}

/** Up/down buttons shared by linked and unlinked rows — `index`/`sectionIds`
 * always describe the entry's position in its full (unfiltered) section, so
 * the search box never changes what a reorder submits. */
function useReorder(entry: RosterManageEntry) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => reorderRoster({ section: entry.section, ids }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['roster', 'manage'] }),
  });
}

function MoveButtons({
  entry,
  sectionIds,
  index,
  onError,
}: {
  entry: RosterManageEntry;
  sectionIds: string[];
  index: number;
  onError: (message: string) => void;
}) {
  const reorderMutation = useReorder(entry);

  function handleMove(direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= sectionIds.length) return;
    const ids = [...sectionIds];
    const moved = ids[index];
    const displaced = ids[target];
    if (moved === undefined || displaced === undefined) return;
    ids[index] = displaced;
    ids[target] = moved;
    onError('');
    reorderMutation.mutate(ids, { onError: (err) => onError(err instanceof Error ? err.message : 'Something went wrong.') });
  }

  return (
    <div className={styles.moveButtons}>
      <Button
        type="button"
        variant="ghost"
        aria-label={`Move ${entry.name} up`}
        disabled={index === 0 || reorderMutation.isPending}
        onClick={() => handleMove(-1)}
      >
        ↑
      </Button>
      <Button
        type="button"
        variant="ghost"
        aria-label={`Move ${entry.name} down`}
        disabled={index === sectionIds.length - 1 || reorderMutation.isPending}
        onClick={() => handleMove(1)}
      >
        ↓
      </Button>
    </div>
  );
}

/** A linked crew member's compact row — editing/removal moved to that
 * person's profile page (the Officer controls panel). */
function LinkedRow({
  entry,
  sectionIds,
  index,
  href,
}: {
  entry: RosterManageEntry;
  sectionIds: string[];
  index: number;
  href: string | null;
}) {
  const [error, setError] = useState<string | null>(null);

  return (
    <article className={styles.row}>
      <div className={styles.rowTop}>
        <div className={styles.member}>
          <img className={styles.flagThumb} src={entry.flag} alt="" />
          <span className={styles.title}>{entry.name}</span>
        </div>
        <MoveButtons entry={entry} sectionIds={sectionIds} index={index} onError={(m) => setError(m || null)} />
      </div>
      {entry.subtitle && <p className={styles.meta}>{entry.subtitle}</p>}
      {error && <p className={styles.error}>{error}</p>}
      <div className={styles.actions}>
        {href ? (
          <>
            <Link to={`/cave${href}`} className={styles.profileLink}>
              Edit
            </Link>
            <Link to={href} className={styles.profileLink}>
              View profile
            </Link>
          </>
        ) : (
          <span className={styles.hint}>No profile yet</span>
        )}
      </div>
    </article>
  );
}

/** An unlinked Hall entry (retired crew, a respected dockmate, a feared
 * sailor) — no account, so it keeps the full inline add/edit/remove UI. */
function UnlinkedRow({
  entry,
  me,
  sectionIds,
  index,
}: {
  entry: RosterManageEntry;
  me: User;
  sectionIds: string[];
  index: number;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(entry.name);
  const [subtitle, setSubtitle] = useState(entry.subtitle);
  const [section, setSection] = useState<RosterSection>(entry.section);
  const [flag, setFlag] = useState(entry.flag);
  const [discordUserId, setDiscordUserId] = useState(entry.discordUserId ?? '');
  const [error, setError] = useState<string | null>(null);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['roster', 'manage'] });
  }

  const saveMutation = useMutation({
    mutationFn: () => {
      const input: Parameters<typeof updateRosterEntry>[1] = {};
      if (name !== entry.name) input.name = name;
      if (subtitle !== entry.subtitle) input.subtitle = subtitle;
      if (section !== entry.section) input.section = section;
      if (flag !== entry.flag) input.flag = flag;
      const trimmedDiscordId = discordUserId.trim();
      const currentDiscordId = entry.discordUserId ?? '';
      if (trimmedDiscordId !== currentDiscordId) {
        input.discordUserId = trimmedDiscordId === '' ? null : trimmedDiscordId;
      }
      return updateRosterEntry(entry.id, input);
    },
    onSuccess: () => {
      invalidate();
      setEditing(false);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Something went wrong.'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteRosterEntry(entry.id),
    onSuccess: invalidate,
    onError: (err) => setError(err instanceof Error ? err.message : 'Something went wrong.'),
  });

  function handleSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    saveMutation.mutate();
  }

  function handleRemove() {
    if (!window.confirm(`Remove ${entry.name} from the roster?`)) return;
    setError(null);
    deleteMutation.mutate();
  }

  const mayActOnLink = canActOnLink(me.role, entry);

  if (editing) {
    return (
      <article className={styles.row}>
        <form className={styles.editForm} onSubmit={handleSave}>
          <div className={styles.field}>
            <label htmlFor={`name-${entry.id}`}>Name</label>
            <input id={`name-${entry.id}`} value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className={styles.field}>
            <label htmlFor={`subtitle-${entry.id}`}>Subtitle</label>
            <input
              id={`subtitle-${entry.id}`}
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
            />
          </div>
          <div className={styles.field}>
            <label htmlFor={`section-${entry.id}`}>Section</label>
            <select
              id={`section-${entry.id}`}
              value={section}
              onChange={(e) => setSection(e.target.value as RosterSection)}
            >
              {SECTION_ORDER.map((s) => (
                <option key={s} value={s}>
                  {SECTION_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.field}>
            <span>Flag</span>
            <FlagPicker value={flag} onChange={setFlag} />
          </div>
          <div className={styles.field}>
            <label htmlFor={`discord-${entry.id}`}>Discord user id (optional)</label>
            <input
              id={`discord-${entry.id}`}
              value={discordUserId}
              onChange={(e) => setDiscordUserId(e.target.value)}
              disabled={!mayActOnLink}
            />
            {!mayActOnLink && (
              <p className={styles.hint}>
                Only a manager who can remove this linked member may change this field.
              </p>
            )}
          </div>
          {error && <p className={styles.error}>{error}</p>}
          <div className={styles.actions}>
            <Button type="submit" variant="primary" disabled={saveMutation.isPending}>
              Save
            </Button>
            <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </article>
    );
  }

  return (
    <article className={styles.row}>
      <div className={styles.rowTop}>
        <div className={styles.member}>
          <img className={styles.flagThumb} src={entry.flag} alt="" />
          <span className={styles.title}>{entry.name}</span>
        </div>
        <MoveButtons entry={entry} sectionIds={sectionIds} index={index} onError={(m) => setError(m || null)} />
      </div>
      {entry.subtitle && <p className={styles.meta}>{entry.subtitle}</p>}
      {error && <p className={styles.error}>{error}</p>}
      <div className={styles.actions}>
        <Button type="button" variant="ghost" onClick={() => setEditing(true)}>
          Edit
        </Button>
        {mayActOnLink && (
          <Button
            type="button"
            variant="danger"
            disabled={deleteMutation.isPending}
            onClick={handleRemove}
          >
            Remove
          </Button>
        )}
      </div>
    </article>
  );
}

function AddEntryForm() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [section, setSection] = useState<RosterSection>('members');
  const [flag, setFlag] = useState(STANDARD_FLAG);
  const [discordUserId, setDiscordUserId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () =>
      createRosterEntry({
        name,
        subtitle,
        section,
        flag,
        discordUserId: discordUserId.trim() === '' ? undefined : discordUserId.trim(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roster', 'manage'] });
      setName('');
      setSubtitle('');
      setSection('members');
      setFlag(STANDARD_FLAG);
      setDiscordUserId('');
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Something went wrong.'),
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    createMutation.mutate();
  }

  return (
    <form className={styles.createForm} onSubmit={handleSubmit}>
      <div className={styles.field}>
        <label htmlFor="new-name">Name</label>
        <input id="new-name" value={name} onChange={(e) => setName(e.target.value)} required />
      </div>
      <div className={styles.field}>
        <label htmlFor="new-subtitle">Subtitle</label>
        <input id="new-subtitle" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
      </div>
      <div className={styles.field}>
        <label htmlFor="new-section">Section</label>
        <select id="new-section" value={section} onChange={(e) => setSection(e.target.value as RosterSection)}>
          {SECTION_ORDER.map((s) => (
            <option key={s} value={s}>
              {SECTION_LABELS[s]}
            </option>
          ))}
        </select>
      </div>
      <div className={styles.field}>
        <span>Flag</span>
        <FlagPicker value={flag} onChange={setFlag} />
      </div>
      <div className={styles.field}>
        <label htmlFor="new-discord">Discord user id (optional)</label>
        <input
          id="new-discord"
          value={discordUserId}
          onChange={(e) => setDiscordUserId(e.target.value)}
        />
      </div>
      {error && <p className={styles.error}>{error}</p>}
      <div className={styles.actions}>
        <Button type="submit" variant="primary" disabled={createMutation.isPending}>
          Add to the Hall
        </Button>
      </div>
    </form>
  );
}

export function CrewPage({ me }: { me: User }) {
  const [search, setSearch] = useState('');
  const recruitsQuery = useQuery({ queryKey: ['recruits'], queryFn: getRecruits });
  const rosterQuery = useQuery({ queryKey: ['roster', 'manage'], queryFn: getRosterManage });

  const recruits = recruitsQuery.data?.recruits ?? [];
  const turnedAwayQuery = useQuery({ queryKey: ['turned-away'], queryFn: getTurnedAway });
  const turnedAway = turnedAwayQuery.data?.people ?? [];
  const allEntries = rosterQuery.data?.entries ?? [];
  // Grouped by the *unfiltered* list, so move up/down always reorders the
  // entry's real neighbours in its section — never a search result's.
  const fullGrouped = useMemo(() => groupBySection(allEntries), [allEntries]);
  const filteredGrouped = useMemo(
    () => groupBySection(filterEntries(allEntries, search)),
    [allEntries, search],
  );

  return (
    <section>
      <h2 className={styles.heading}>Crew</h2>

      <h3 className={styles.subheading}>Pending recruits</h3>
      {recruitsQuery.isLoading && <p>Loading…</p>}
      {recruitsQuery.isError && <p className={styles.error}>{(recruitsQuery.error as Error).message}</p>}
      {recruitsQuery.isSuccess && recruits.length === 0 && (
        <p className={styles.empty}>No one is waiting on a decision.</p>
      )}
      <div className={styles.list}>
        {recruits.map((recruit) => (
          <RecruitRow key={recruit.id} recruit={recruit} />
        ))}
      </div>

      <h3 className={styles.subheading}>Turned away</h3>
      {turnedAwayQuery.isSuccess && turnedAway.length === 0 && (
        <p className={styles.empty}>Nobody has been rejected or removed.</p>
      )}
      {turnedAwayQuery.isError && <p className={styles.error}>{(turnedAwayQuery.error as Error).message}</p>}
      <div className={styles.list}>
        {turnedAway.map((person) => (
          <TurnedAwayRow key={person.id} person={person} />
        ))}
      </div>

      <h3 className={styles.subheading}>Add to the Hall</h3>
      <AddEntryForm />

      <h3 className={styles.subheading}>Crew list</h3>
      <input
        type="search"
        className={styles.search}
        placeholder="Search the crew…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        aria-label="Search the crew"
      />
      {rosterQuery.isLoading && <p>Loading…</p>}
      {rosterQuery.isError && <p className={styles.error}>{(rosterQuery.error as Error).message}</p>}
      {SECTION_ORDER.map((section) => {
        const entries = filteredGrouped[section];
        const fullSectionIds = fullGrouped[section].map((e) => e.id);
        return (
          <div key={section} className={styles.sectionBlock}>
            <h4 className={styles.subsubheading}>{SECTION_LABELS[section]}</h4>
            {entries.length === 0 && (
              <p className={styles.empty}>{search.trim() ? 'No one matches.' : 'Nobody here yet.'}</p>
            )}
            <div className={styles.list}>
              {entries.map((entry) => {
                const index = fullSectionIds.indexOf(entry.id);
                // compact row only for members with a profile (edited via its Officer
                // controls); every other entry stays fully editable here
                const href = profileHref(entry);
                return href ? (
                  <LinkedRow key={entry.id} entry={entry} sectionIds={fullSectionIds} index={index} href={href} />
                ) : (
                  <UnlinkedRow
                    key={entry.id}
                    entry={entry}
                    me={me}
                    sectionIds={fullSectionIds}
                    index={index}
                  />
                );
              })}
            </div>
          </div>
        );
      })}

      <h3 className={styles.subheading}>Uploaded flags</h3>
      <UploadedFlags />
    </section>
  );
}
